'use client'
import * as React from 'react'
import { Check, Copy, Download, QrCode, RotateCcw, ShieldCheck, Link2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ColorPicker } from '@/components/ui/color-picker'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { encodeQR, qrToSvg } from '@/lib/widget/qr'
import { appSignupUrl } from '@/lib/widget/embed'

// Pure-browser QR generator for UGC collection. The visitor enters the
// destination (a collect URL), a brand colour and an optional printable
// caption; the component encodes a real, scannable QR (self-contained
// encoder in lib/widget/qr.ts) and offers PNG / SVG download. Honest
// split: the QR is real and works today; the submission page it points
// to is minted by the Idukki app (rights + consent handled there).

const SAMPLE_URL = 'https://app.idukki.io/collect/your-store'
const DEFAULT_DARK = '#0b1120' // brand navy, as data for the colour picker
const QUIET_ZONE = 4

// Resolve a CSS custom property to a concrete colour so the rendered /
// downloaded QR carries a real value (the brand-lime token by default).
function brandLimeHex(): string {
  if (typeof window === 'undefined') return '#CDEB45'
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--brand-lime').trim()
  // token is stored as HSL triplet "79 100% 66%"
  const m = raw.match(/^([\d.]+)\s+([\d.]+)%\s+([\d.]+)%$/)
  if (!m) return '#CDEB45'
  const h = Number(m[1]) / 360
  const s = Number(m[2]) / 100
  const l = Number(m[3]) / 100
  const f = (n: number) => {
    const k = (n + h * 12) % 12
    const a = s * Math.min(l, 1 - l)
    const v = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))
    return Math.round(255 * v)
  }
  const toHex = (n: number) => n.toString(16).padStart(2, '0')
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`
}

export function QrUgcGenerator() {
  const [url, setUrl] = React.useState(SAMPLE_URL)
  const [dark, setDark] = React.useState(DEFAULT_DARK)
  const [caption, setCaption] = React.useState('Snap your unboxing — tag us to be featured')
  const [copied, setCopied] = React.useState(false)

  const trimmed = url.trim() || SAMPLE_URL

  const matrix = React.useMemo(() => {
    try {
      return encodeQR(trimmed)
    } catch {
      return null
    }
  }, [trimmed])
  const tooLong = matrix === null

  const svgMarkup = React.useMemo(
    () => (matrix ? qrToSvg(matrix, { dark, light: '#ffffff', size: 320, quietZone: QUIET_ZONE }) : ''),
    [matrix, dark],
  )

  function downloadBlob(blob: Blob, filename: string) {
    const href = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = href
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(href)
  }

  function onDownloadSvg() {
    if (!matrix) return
    const blob = new Blob([qrToSvg(matrix, { dark, light: '#ffffff', size: 1024, quietZone: QUIET_ZONE })], {
      type: 'image/svg+xml',
    })
    downloadBlob(blob, 'idukki-ugc-qr.svg')
  }

  function onDownloadPng() {
    if (!matrix) return
    const px = 1024
    const canvas = document.createElement('canvas')
    canvas.width = px
    canvas.height = px
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const count = matrix.length
    const total = count + QUIET_ZONE * 2
    const scale = px / total
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, px, px)
    ctx.fillStyle = dark
    for (let r = 0; r < count; r++) {
      for (let c = 0; c < count; c++) {
        if (matrix[r][c]) {
          ctx.fillRect((c + QUIET_ZONE) * scale, (r + QUIET_ZONE) * scale, scale + 0.5, scale + 0.5)
        }
      }
    }
    canvas.toBlob((blob) => {
      if (blob) downloadBlob(blob, 'idukki-ugc-qr.png')
    }, 'image/png')
  }

  function onCopyUrl() {
    navigator.clipboard?.writeText(trimmed).then(
      () => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      },
      () => {},
    )
  }

  function reset() {
    setUrl(SAMPLE_URL)
    setDark(DEFAULT_DARK)
    setCaption('Snap your unboxing — tag us to be featured')
  }

  return (
    <div className="rounded-2xl border bg-card shadow-sm">
      <div className="flex items-center justify-between border-b p-5 sm:p-6">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">QR collector</p>
          <h2 className="mt-1 font-display text-xl font-medium tracking-tight sm:text-2xl">
            Generate a scannable QR for your collection page
          </h2>
        </div>
        <button
          type="button"
          onClick={reset}
          className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs text-muted-foreground transition hover:border-primary/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <RotateCcw className="h-3 w-3" aria-hidden /> Reset
        </button>
      </div>

      <div className="grid gap-6 p-5 sm:p-6 md:grid-cols-2 md:gap-8 md:p-8">
        {/* Controls */}
        <div className="space-y-5">
          <Field label="Destination URL" hint="Where the camera sends customers, your Idukki collect link">
            <div className="relative">
              <Link2
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                id="qr-url"
                type="url"
                inputMode="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={SAMPLE_URL}
                className="pl-9"
              />
            </div>
            {tooLong && (
              <p className="mt-1 text-[11px] text-destructive">
                That URL is too long to encode. Shorten it (a short collect link scans far more reliably on packaging).
              </p>
            )}
          </Field>

          <Field label="QR colour" hint="Dark modules. Keep strong contrast against white so it scans">
            <div className="flex flex-wrap items-center gap-2">
              <ColorPicker value={dark} onChange={setDark} ariaLabel="QR colour" />
              <Swatch label="Navy" value="#0b1120" current={dark} onPick={setDark} />
              <Swatch label="Lime" value={brandLimeHex()} current={dark} onPick={setDark} />
              <Swatch label="Ink" value="#111111" current={dark} onPick={setDark} />
            </div>
          </Field>

          <Field label="Printable caption (optional)" hint="Shown under the QR on the printable card, a clear ask lifts scans">
            <Input
              id="qr-caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Snap your unboxing — tag us to be featured"
            />
          </Field>

          <div className="flex flex-wrap gap-2 pt-1">
            <Button type="button" size="sm" onClick={onDownloadPng} disabled={!matrix}>
              <Download className="h-3.5 w-3.5" aria-hidden /> Download PNG
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={onDownloadSvg} disabled={!matrix}>
              <Download className="h-3.5 w-3.5" aria-hidden /> Download SVG
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={onCopyUrl}>
              {copied ? (
                <>
                  <Check className="h-3.5 w-3.5" aria-hidden /> Copied
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" aria-hidden /> Copy URL
                </>
              )}
            </Button>
            <span aria-live="polite" className="sr-only">
              {copied ? 'URL copied to clipboard' : ''}
            </span>
          </div>
        </div>

        {/* Printable card preview */}
        <div className="flex flex-col items-center justify-center">
          <div className="w-full max-w-[300px] rounded-2xl border bg-background p-6 text-center shadow-sm">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Scan to share your photos
            </p>
            <div className="mx-auto mt-4 w-full max-w-[220px]">
              {matrix ? (
                <div
                  className="aspect-square w-full overflow-hidden rounded-xl border bg-white"
                  // The SVG is generated by our own encoder from sanitised
                  // inputs (URL string + a colour value); no external HTML.
                  dangerouslySetInnerHTML={{ __html: svgMarkup }}
                  role="img"
                  aria-label={`QR code linking to ${trimmed}`}
                />
              ) : (
                <div className="flex aspect-square w-full items-center justify-center rounded-xl border border-dashed text-xs text-muted-foreground">
                  Enter a shorter URL
                </div>
              )}
            </div>
            {caption.trim() && (
              <p className="mt-4 text-sm font-medium leading-snug text-foreground">{caption.trim()}</p>
            )}
            <p className="mt-3 truncate text-[11px] text-muted-foreground" title={trimmed}>
              {trimmed.replace(/^https?:\/\//, '')}
            </p>
          </div>
          <p className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <QrCode className="h-3 w-3 text-primary" aria-hidden />
            Real, scannable QR. Test it with your phone camera before printing.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3 border-t bg-secondary/30 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <p className="max-w-xl text-xs text-muted-foreground sm:text-sm">
          <ShieldCheck className="mr-1 inline h-3.5 w-3.5 text-primary" aria-hidden />
          The QR is generated in your browser and works today. The page it opens (photo + video upload, rights and
          consent capture, auto-tagging) is minted by Idukki when you connect a store.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button asChild size="sm">
            <a href={appSignupUrl({ source: 'upload', ref: 'tool-qr-ugc' })}>Set up a branded collector</a>
          </Button>
        </div>
      </div>
    </div>
  )
}

function Swatch({
  label,
  value,
  current,
  onPick,
}: {
  label: string
  value: string
  current: string
  onPick: (v: string) => void
}) {
  const active = current.toLowerCase() === value.toLowerCase()
  return (
    <button
      type="button"
      onClick={() => onPick(value)}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        active ? 'border-primary bg-primary/10 text-foreground' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      <span className="h-3 w-3 rounded-full border" style={{ backgroundColor: value }} aria-hidden />
      {label}
    </button>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
      {hint && <span className="mt-0.5 block text-[11px] text-muted-foreground">{hint}</span>}
      <div className="mt-1.5">{children}</div>
    </label>
  )
}
